import React from 'react';
import { X, Upload, Eye, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type UploadStep = 'validating' | 'checking-duplicates' | 'checking-content' | 'uploading' | 'moderating' | 'saving' | 'complete' | 'error';

export interface UploadProgressState {
  step: UploadStep;
  progress: number;
  message: string;
  canCancel: boolean;
  error?: string;
  estimatedTimeRemaining?: number;
}

interface UploadProgressModalProps {
  isOpen: boolean;
  state: UploadProgressState;
  onCancel: () => void;
  onRetry?: () => void;
}

const STEP_CONFIG = {
  validating: {
    icon: AlertCircle,
    label: 'Validating',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/20'
  },
  'checking-duplicates': {
    icon: Eye,
    label: 'Checking Duplicates',
    color: 'text-orange-400',
    bgColor: 'bg-orange-400/20'
  },
  'checking-content': {
    icon: AlertCircle,
    label: 'Checking Content',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/20'
  },
  uploading: {
    icon: Upload,
    label: 'Uploading Media',
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/20'
  },
  moderating: {
    icon: Eye,
    label: 'Content Review',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/20'
  },
  saving: {
    icon: Save,
    label: 'Saving Post',
    color: 'text-green-400',
    bgColor: 'bg-green-400/20'
  },
  complete: {
    icon: CheckCircle,
    label: 'Complete',
    color: 'text-green-400',
    bgColor: 'bg-green-400/20'
  },
  error: {
    icon: AlertCircle,
    label: 'Failed',
    color: 'text-red-400',
    bgColor: 'bg-red-400/20'
  }
};

const UploadProgressModal: React.FC<UploadProgressModalProps> = ({
  isOpen,
  state,
  onCancel,
  onRetry
}) => {
  if (!isOpen) return null;

  const currentStepConfig = STEP_CONFIG[state.step];
  const IconComponent = currentStepConfig.icon;

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStepIndex = (step: UploadStep) => {
    const stepOrder = ['validating', 'checking-duplicates', 'checking-content', 'uploading', 'moderating', 'saving'];
    return stepOrder.indexOf(step);
  };

  const currentStepIndex = getStepIndex(state.step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#1A1F2C] border border-white/10 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Posting Content</h3>
          {state.canCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="space-y-4 mb-6">
          {['validating', 'checking-duplicates', 'checking-content', 'uploading', 'moderating', 'saving'].map((step, index) => {
            const stepConfig = STEP_CONFIG[step as UploadStep];
            const StepIcon = stepConfig.icon;
            const isActive = state.step === step;
            const isCompleted = currentStepIndex > index && state.step !== 'error';
            const isError = state.step === 'error' && isActive;

            return (
              <div key={step} className="flex items-center gap-3">
                <div className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all",
                  isCompleted ? "bg-green-400/20 border-green-400 text-green-400" :
                  isActive && !isError ? `${stepConfig.bgColor} border-current ${stepConfig.color}` :
                  isError ? "bg-red-400/20 border-red-400 text-red-400" :
                  "border-gray-600 text-gray-500"
                )}>
                  <StepIcon className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className={cn(
                    "font-medium",
                    isCompleted ? "text-green-400" :
                    isActive && !isError ? stepConfig.color :
                    isError ? "text-red-400" :
                    "text-gray-500"
                  )}>
                    {stepConfig.label}
                  </div>
                  {isActive && state.message && (
                    <div className="text-sm text-gray-400 mt-1">
                      {state.message}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress Bar */}
        {state.step !== 'error' && state.step !== 'complete' && (
          <div className="mb-4">
            <Progress 
              value={state.progress} 
              className="h-2 bg-white/10"
            />
            <div className="flex justify-between items-center mt-2 text-xs text-gray-400">
              <span>{state.progress}% complete</span>
              {state.estimatedTimeRemaining && state.estimatedTimeRemaining > 0 && (
                <span>~{formatTime(state.estimatedTimeRemaining)} remaining</span>
              )}
            </div>
          </div>
        )}

        {/* Error State */}
        {state.step === 'error' && (
          <div className="mb-4 p-3 bg-red-400/10 border border-red-400/20 rounded-lg">
            <div className="text-red-400 font-medium mb-1">Upload Failed</div>
            <div className="text-sm text-gray-300">
              {state.error || 'An unexpected error occurred. Please try again.'}
            </div>
          </div>
        )}

        {/* Success State */}
        {state.step === 'complete' && (
          <div className="mb-4 p-3 bg-green-400/10 border border-green-400/20 rounded-lg">
            <div className="text-green-400 font-medium">Post Successful!</div>
            <div className="text-sm text-gray-300">Your content has been posted.</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {state.step === 'error' && onRetry && (
            <Button 
              onClick={onRetry}
              className="flex-1"
              variant="default"
            >
              Try Again
            </Button>
          )}
          
          {state.step === 'complete' ? (
            <Button 
              onClick={onCancel}
              className="flex-1"
              variant="default"
            >
              Close
            </Button>
          ) : state.canCancel && (
            <Button 
              onClick={onCancel}
              className="flex-1"
              variant="outline"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadProgressModal;